/**
 * Canonical list of the 7 SEO clusters + the state-overview aggregator.
 * Single source of truth for the global header "Research" dropdown,
 * the homepage cluster grid, and the footer "Research tools" section.
 *
 * Adding a new cluster: add one entry below and it surfaces in all 3
 * places without further code changes.
 */

export const RESEARCH_CLUSTERS = [
  {
    slug: "quote-fairness",
    label: "Is my contractor quote fair?",
    shortLabel: "Quote fairness",
    path: "/quote-fairness-calculator",
    emoji: "⚖️",
    description: "Compare any contractor quote against the regional state × project benchmark.",
  },
  {
    slug: "renovation-timeline",
    label: "How long will my renovation take?",
    shortLabel: "Renovation timeline",
    path: "/renovation-timeline",
    emoji: "⏱️",
    description: "Typical start-to-finish length for 11 project types across all 50 states.",
  },
  {
    slug: "contractor-licensing",
    label: "Does my contractor need a license?",
    shortLabel: "Contractor licensing",
    path: "/contractor-licensing",
    emoji: "📜",
    description: "State-by-state contractor license + residential permit requirements.",
  },
  {
    slug: "contractor-deposit-rules",
    label: "How much deposit can a contractor ask for?",
    shortLabel: "Deposit rules",
    path: "/contractor-deposit-rules",
    emoji: "💵",
    description: "Statutory deposit caps by state (CA: 10% / $1K, MD/MA/CT/ME/NY/PA: 1/3, etc.).",
  },
  {
    slug: "energy-rebates",
    label: "What rebates can I claim?",
    shortLabel: "Energy rebates",
    path: "/energy-rebates",
    emoji: "🔋",
    description: "IRA HEEHRA + state rebate stack for 8 energy upgrades (federal 25C/25D credits expired 2025).",
  },
  {
    slug: "permit-cost",
    label: "How much will the permit cost?",
    shortLabel: "Permit cost",
    path: "/permit-cost",
    emoji: "🏛️",
    description: "Residential permit fees for 11 project types across all 50 states.",
  },
  {
    slug: "basement-finish-roi",
    label: "Should I finish my basement?",
    shortLabel: "Basement-finish ROI",
    path: "/basement-finish-roi",
    emoji: "🏠",
    description: "5-tier ROI verdict for 5 basement use scopes across all 50 states (250 deep-link pages).",
  },
];

export const STATE_OVERVIEW_LINK = {
  slug: "state-overview",
  label: "All clusters in one view — by state",
  shortLabel: "State overview",
  path: "/state-overview",
  emoji: "🗺️",
  description: "One page per state · 6 lenses · fair price / timeline / licensing / deposits / permits / rebates.",
};

/**
 * Standalone free utilities that are NOT cluster hubs but DO deserve
 * top-level discovery — they answer a homeowner question in a single
 * page without a leaf-cluster behind them. Surfaced in the header
 * "Research" dropdown footer + the homepage "Free tools" rail + the
 * /tools index page.
 */
export const FREE_TOOLS = [
  {
    slug: "state-utility-rebate-lookup",
    label: "What smart-home rebates can I claim in my state?",
    shortLabel: "Rebate lookup",
    path: "/state-utility-rebate-lookup",
    emoji: "💰",
    description: "Smart-thermostat / EV-charger / heat-pump / smart-sprinkler rebates from 50+ utilities across 22 states.",
  },
];

/**
 * "Pillar guides" — the highest-leverage long-form research pages that
 * deserve homepage + footer prominence. These are the recently-shipped
 * pillars whose inbound-link counts were lagging (GSC "Discovered —
 * currently not crawled") because they only existed in chronological
 * /guides listings. Surfacing them in the centralized registry gives
 * each pillar:
 *   - One prominent homepage card (above-the-fold research rail)
 *   - One footer link (auto-replicated via .map())
 *   - One header-dropdown entry (when wired)
 *
 * Adding a new pillar: append here and it surfaces in all 3 places
 * without further code changes.
 */
export const PILLAR_GUIDES = [
  {
    slug: "cost-index-by-state-2026",
    label: "2026 home renovation cost index, all 50 states ranked",
    shortLabel: "2026 cost index by state",
    path: "/guides/2026-home-renovation-cost-index-by-state",
    emoji: "📊",
    description: "All 50 states ranked by 2026 renovation cost multiplier (HI 1.55× → MS 0.84×), with per-project $ examples and structural cost-driver narrative.",
  },
  {
    slug: "solar-panel-roi-by-state-2026",
    label: "2026 solar panel ROI + payback by state",
    shortLabel: "2026 solar payback by state",
    path: "/guides/solar-panel-roi-by-state-2026",
    emoji: "☀️",
    description: "State-by-state solar PV payback windows for 2026, factoring gross cost, net-metering rates, state rebates, and roof-suitability variance.",
  },
  {
    slug: "renovate-or-move-calculator",
    label: "Renovate or move? 5/10/20-year cost math",
    shortLabel: "Renovate or move? (calculator)",
    path: "/renovate-or-move-calculator",
    emoji: "🔁",
    description: "11-input decision tool — runs the 5/10/20-year cost math factoring locked-rate mortgages, cap-gains exemption, and CA Prop 13 / TX / FL property-tax reset.",
  },
];
